"use client";

import React, { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { FiChevronLeft, FiChevronRight, FiCalendar } from "react-icons/fi";
import { getPublishedEvents } from "@/api/eventService";
import { EventData } from "@/models/api/event";
import EventBannerImage from "./EventBannerImage";
import styles from "./EventSection.module.css";
const ITEMS_PER_PAGE = 6; // 3 per row × 2 rows

const formatEventDate = (iso: string) => {
  if (!iso) return "";

  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";

  return d.toLocaleString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatEventDuration = (startIso: string, endIso?: string) => {
  const start = formatEventDate(startIso);
  const end = endIso ? formatEventDate(endIso) : "";

  if (start && end && start !== end) {
    return `${start} - ${end}`;
  }
  return start;
};

const getEventStatus = (event: EventData): "Upcoming" | "Live" | "Past" => {
  const now = new Date();
  const startDate = new Date(event.startDateTime);
  const endDate = event.endDateTime ? new Date(event.endDateTime) : null;

  if (endDate && !isNaN(endDate.getTime())) {
    if (now < startDate) return "Upcoming";
    if (now > endDate) return "Past";
    return "Live";
  } else {
    // Fallback if no valid end date is provided
    if (now < startDate) return "Upcoming";

    // Check if it's the same day
    if (
      now.getFullYear() === startDate.getFullYear() &&
      now.getMonth() === startDate.getMonth() &&
      now.getDate() === startDate.getDate()
    ) {
      return "Live";
    }

    return "Past";
  }
};

const getLabelClass = (status: "Upcoming" | "Live" | "Past") => {
  switch (status) {
    case "Upcoming":
      return styles.labelUpcoming;
    case "Live":
      return styles.labelLive;
    case "Past":
      return styles.labelPast;
    default:
      return "";
  }
};


const EventSection: React.FC = () => {
  const [events, setEvents] = useState<EventData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);

    getPublishedEvents()
      .then((data) => {
        if (!isMounted) return;
        setEvents(data);
      })
      .catch(() => {
        if (isMounted) setError("We are currently experiencing a technical issue and couldn't retrieve the event schedule. Please try again or check back shortly.");
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [retryCount]);

  const router = useRouter();
  const searchQuery = (router.query.search as string || "").toLowerCase();

  const sortedEvents = useMemo(() => {
    let filtered = events;

    // Filter by search query if it exists
    if (searchQuery) {
      filtered = filtered.filter((event) =>
        event.title?.toLowerCase().includes(searchQuery) ||
        event.description?.toLowerCase().includes(searchQuery)
      );
    }

    return [...filtered].sort((a, b) => {
      const statusA = getEventStatus(a);
      const statusB = getEventStatus(b);

      const statusOrder = {
        "Live": 1,
        "Upcoming": 2,
        "Past": 3
      };

      if (statusOrder[statusA] !== statusOrder[statusB]) {
        return statusOrder[statusA] - statusOrder[statusB];
      }

      // If statuses are the same, sort by start date
      const timeA = new Date(a.startDateTime).getTime();
      const timeB = new Date(b.startDateTime).getTime();

      if (statusA === "Upcoming" || statusA === "Live") {
        return timeA - timeB; // Ascending: closest upcoming/live events first
      } else {
        return timeB - timeA; // Descending: most recent past events first
      }
    });
  }, [events, searchQuery]);

  // Reset to page 1 when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const totalPages = Math.ceil(sortedEvents.length / ITEMS_PER_PAGE);

  const paginatedEvents = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return sortedEvents.slice(start, start + ITEMS_PER_PAGE);
  }, [sortedEvents, currentPage]);

  return (
    <section className="home-news pb-100 pt-100">
      <div className="container">
        {loading && (
          <div className={styles.loaderWrapper}>
            <div className={styles.spinner}></div>
          </div>
        )}

        {!loading && error && (
          <div className={`empty-state-wrapper text-center ${styles.errorWrapper}`}>
            <div className={styles.errorIconContainer}>
              <span className={styles.errorIconText}>!</span>
            </div>
            <h3 className={styles.errorTitle}>Unable to Load Events</h3>


          </div>
        )}

        {!loading && !error && events.length === 0 && (
          <div className={`empty-state-wrapper text-center ${styles.emptyWrapper}`}>
            <div className={styles.emptyIconContainer}>
              <FiCalendar size={40} />
            </div>
            <h3 className={styles.emptyTitle}>No Events Currently Scheduled</h3>
            <p className={styles.emptyText}>
              We don't have any events to display at the moment. Please stay tuned for future announcements and upcoming activities.
            </p>
          </div>
        )}

        {!loading && !error && events.length > 0 && (
          <>
            <div className="row">
              {paginatedEvents.map((event) => (
                <div key={event.id} className="col-lg-4 col-md-6 mb-4">
                  <div className="single-home-news h-100 d-flex flex-column">
                    <a href={`/eventdetail?id=${event.id}`} onClick={(e) => { e.preventDefault(); router.push(`/eventdetail?id=${event.id}`); }}>
                      <div className={styles.eventLabelContainer}>
                        <EventBannerImage
                          imageUrl={event.fileUrl}
                          title={event.title}
                          className={`img-fluid ${styles.tileImage}`}
                        />
                        <span className={`${styles.eventLabel} ${getLabelClass(getEventStatus(event))}`}>
                          {getEventStatus(event)}
                        </span>
                      </div>
                    </a>

                    <div className="single-home-content flex-grow-1">
                      <a href={`/eventdetail?id=${event.id}`} onClick={(e) => { e.preventDefault(); router.push(`/eventdetail?id=${event.id}`); }}>
                        <h2
                          className="text-line-clamp-2"
                          title={event.title}
                        >
                          {event.title}
                        </h2>
                      </a>
                      <p className="calender">
                        <i className="flaticon-calendar"></i> {formatEventDuration(event.startDateTime, event.endDateTime)}
                      </p>
                      <p
                        className="text-line-clamp-3"
                        title={event.description}
                      >
                        {event.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="pagination-wrapper">
                <button
                  className="pagination-arrow"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                >
                  <FiChevronLeft />
                </button>

                {[...Array(totalPages)].map((_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      className={`pagination-number ${page === currentPage ? "active" : ""}`}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  );
                })}

                <button
                  className="pagination-arrow"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                >
                  <FiChevronRight />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default EventSection;
